import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types'
import { useDispatch } from "react-redux";

import TextField from '@material-ui/core/TextField';

import makePath from '../../utils/makePath';
import generateId from '../../utils/generateId';
import { setItem } from "../../actions";
import { useDirty, useFormElementCleanUp, useValidation} from '../../hooks';
import { FormElementWrapper } from './FormElementWrapper';
import { FormHelperText } from '@material-ui/core';


export const NumericInput = React.memo(({decimalPlaces,defaultNumber = '',given,label,max,min,name,optional, ...props}) => {
  const [value, setValue] = useState(decimalPlaces ? defaultNumber.toFixed(decimalPlaces) : defaultNumber);
  const [id] = useState(generateId('numericInput-'));

  const dispatch = useDispatch();
  const setDirty = useDirty();
  
  const path = makePath(props.path,name);
  useFormElementCleanUp(path);

  // Validation conditions
  const validate = () => {

    if ((!value && value !== 0) && !optional) {
      setError('Missing! This field is required.');
    } else if ( (typeof min === 'number' && value < min) || (typeof max === 'number' && value > max) ) {
      setError('Error! You need to adjust your value to be within the defined range.');
    } else {
      setError('')
    }
    
  }
  
  // Apply validation conditions
  const [error, setError] = useValidation(validate,value,path,id);

  // Set user input in state, if criteria are met
  const handleChange = (e) => {
    // Make sure we hav a number
    let newValue = parseFloat(e.target.value);

    // Allow an empty value so that users can 
    // clear the field before typing a new number
    if (e.target.value === '') {
      setDirty();
      setValue(e.target.value)
    } 
    // Make sure number is between allowed values
    else if (
        e.target.value.length > 5 ||
        (isNaN(newValue) && newValue !== '.') ||
        ((max || max === 0) && newValue > max) ||
        ((min || min === 0) && newValue < min)
      ) {
        return;
    } else {
      setDirty();
      setValue(newValue)
    }

  }

  // Update store when state changes
  useEffect(() => {
    dispatch(setItem(path, value));
  }, [dispatch,path,value])


  // Set state from given value, if provided
  useEffect(() => {
    if (given) {
      setValue(given);
    }
  }, [given]);

  // Special increment/decrement handling with up/down arrow keys
  // when decimal places are used
  const handleKeyDown = (e) => {
    const val100 = value * 100;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (val100 - 100 >= min * 100) {
        setValue( ((val100 - 100) / 100).toFixed(decimalPlaces) )
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (val100 + 100 >= min * 100) {
        setValue( ((val100 + 100) / 100).toFixed(decimalPlaces) )
      }
    }
  } 

  const InputProps = {'aria-describedby': `${id}-helper-text ${id}-additional-helper-text`}
  if (decimalPlaces) {
    InputProps.onKeyDown = handleKeyDown;
  }

  return (
    <FormElementWrapper>
      <TextField 
        variant="outlined"
        autoComplete="off"
        id={id}
        label={label} 
        name={name}
        value={value}
        type="number"
        min={min}
        max={max}
        onChange={handleChange} 
        helperText={error}
        error={!!error}
        InputProps={InputProps}
      />

      {/* Additional help text so that built-in "helperText" can be used for error */}
      {props.help ? 
        <FormHelperText id={`${id}-additional-helper-text`}>
          {props.help}
        </FormHelperText>
      : '' }

    </FormElementWrapper>
  )
  
})


NumericInput.propTypes = {
  decimalPlaces: PropTypes.number,
  defaultNumber: PropTypes.number,
  help: PropTypes.string,
  label: PropTypes.string.isRequired,
  max: PropTypes.number,
  min: PropTypes.number,
  name: PropTypes.string.isRequired,
  path: PropTypes.string
}